import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAgency, yearOf } from '../lib/AgencyContext';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';
import ImportInvoices from './ImportInvoices';
import { StatusBadge, StatusActions, normalizeStatus, statusMeta, paidRSD } from './InvoiceStatus';
import { sefLabel } from './SefSendDialog';
import { formatRSD, formatCur, fmtDate, toRSD } from '../lib/helpers';
import { searchKey } from '../lib/search';

// ============================================================
// LISTA FAKTURA — svedena tabela sa filterima
// Vrh: tri kartice sa limitima (6 mil. fakturisano · čeka naplatu · 8 mil. naplaćeno za 365 dana)
// Filteri: status (dugmići sa brojem) · pretraga · vrsta · mesec · redosled · broj po strani
// ============================================================

const LIMIT_YEAR = 6000000;   // paušalac: promet u kalendarskoj godini
const LIMIT_365 = 8000000;    // paušalac: naplaćeno u poslednjih 365 dana

// Pretraga bez obzira na pismo (ćirilica/latinica), velika/mala slova i kvačice
const norm = searchKey;

const LOCALES = { sr: 'sr-Latn-RS', en: 'en-GB', ru: 'ru-RU' };

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const daysBetween = (fromISO, toISO) =>
  Math.round((new Date(`${toISO}T00:00:00`) - new Date(`${fromISO}T00:00:00`)) / 86400000);

export default function InvoicingList() {
  const { activeAgencyId, year } = useAgency();
  const { t, tr, lang } = useI18n();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterKind, setFilterKind] = useState('sve');
  // Podmeni: /fakturisanje?vrsta=faktura | profaktura
  const location = useLocation();
  const vrsta = new URLSearchParams(location.search).get('vrsta');
  useEffect(() => {
    setFilterKind(vrsta === 'faktura' || vrsta === 'profaktura' ? vrsta : 'sve');
  }, [vrsta]);
  const [filterStatus, setFilterStatus] = useState('sve');
  const [filterMonth, setFilterMonth] = useState('sve');
  const [query, setQuery] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [newestFirst, setNewestFirst] = useState(true); // pri svakom ulasku: poslednja faktura gore
  const [perPage, setPerPage] = useState(25);
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const meta = statusMeta(t);
  const locale = LOCALES[lang] || LOCALES.sr;

  const KIND_LABELS = { faktura: t('kindInvoice'), profaktura: t('kindProforma'), fiskalni: t('kindFiscal') };

  // Profakture koje su fakturisane: id profakture → faktura napravljena od nje (stornirane se ne računaju)
  const invoiceOfProforma = useMemo(() => {
    const map = {};
    invoices.forEach((i) => {
      if (i.source_proforma_id && normalizeStatus(i.status) !== 'stornirano') map[i.source_proforma_id] = i;
    });
    return map;
  }, [invoices]);

  useEffect(() => {
    if (!activeAgencyId) return;
    supabase
      .from('invoices')
      .select('*')
      .eq('agency_id', activeAgencyId)
      .order('issue_date', { ascending: false })
      .then(({ data }) => {
        setInvoices(data || []);
        setLoading(false);
      });
  }, [activeAgencyId, reloadKey]);

  // Promena filtera vraća na prvu stranu
  useEffect(() => { setPage(1); }, [filterKind, filterStatus, filterMonth, query, perPage, year]);

  // Redni broj iz broja fakture: "03/2026" → 3, "2026-003" → 3
  const seqOf = (inv) => {
    const n = String(inv.invoice_number || '').trim();
    let m = n.match(/^(\d+)\s*[/.-]\s*\d{4}$/);
    if (m) return parseInt(m[1], 10);
    m = n.match(/^\d{4}\s*[/.-]\s*(\d+)$/);
    if (m) return parseInt(m[1], 10);
    m = n.match(/(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  };

  const today = todayISO();
  const isUnpaid = (i) => i.document_kind !== 'profaktura' && ['poslato', 'delimicno'].includes(normalizeStatus(i.status));
  const overdueDays = (i) => (isUnpaid(i) && i.due_date && i.due_date < today ? daysBetween(i.due_date, today) : 0);
  // Ulazi u zbir: sve osim profaktura i storniranih
  const counts = (i) => i.document_kind !== 'profaktura' && normalizeStatus(i.status) !== 'stornirano';
  // Preostalo za naplatu u dinarima (kod delimično plaćenih samo ostatak)
  const remainingRSD = (i) => {
    const full = toRSD(i);
    const amt = Number(i.amount) || 0;
    const paid = Number(i.paid_amount) || 0;
    if (normalizeStatus(i.status) === 'delimicno' && amt > 0) return full * Math.max(0, (amt - paid) / amt);
    return full;
  };

  // ---------- kartice sa limitima ----------
  const yearDocs = invoices.filter((i) => yearOf(i.issue_date) === year);
  const totalYear = yearDocs.filter(counts).reduce((s, i) => s + toRSD(i), 0);
  const unpaidDocs = yearDocs.filter(isUnpaid);
  const unpaidSum = unpaidDocs.reduce((s, i) => s + remainingRSD(i), 0);
  const overdueDocs = unpaidDocs.filter((i) => overdueDays(i) > 0);
  const overdueSum = overdueDocs.reduce((s, i) => s + remainingRSD(i), 0);
  // Naplaćeno u poslednjih 365 dana — po datumu uplate (sve godine)
  const from365 = (() => { const d = new Date(); d.setDate(d.getDate() - 365); return d.toISOString().slice(0, 10); })();
  const paid365 = invoices
    .filter((i) => i.document_kind !== 'profaktura' && i.paid_at && String(i.paid_at).slice(0, 10) >= from365 && String(i.paid_at).slice(0, 10) <= today)
    .reduce((s, i) => s + paidRSD(i), 0);

  // ---------- filteri ----------
  const q = norm(query.trim());
  const baseDocs = yearDocs.filter((i) => i.document_kind !== 'pazar' && (filterKind === 'sve' || i.document_kind === filterKind)
    && (filterMonth === 'sve' || String(i.issue_date || '').slice(0, 7) === filterMonth)
    && (!q || norm(`${i.invoice_number} ${i.client} ${i.description}`).includes(q)));

  const STATUS_TABS = [
    ['sve', t('stAll')],
    ['u_pripremi', meta.u_pripremi.label],
    ['poslato', meta.poslato.label],
    ['kasni', tr('Kasni', 'Overdue', 'Просрочено')],
    ['naplaceno', meta.naplaceno.label],
    ['delimicno', meta.delimicno.label],
    ['stornirano', meta.stornirano.label],
  ];
  const matchStatus = (i, key) => {
    if (key === 'sve') return true;
    if (key === 'kasni') return overdueDays(i) > 0;
    return i.document_kind !== 'profaktura' && normalizeStatus(i.status) === key;
  };
  const tabCount = (key) => baseDocs.filter((i) => matchStatus(i, key)).length;

  const filtered = baseDocs
    .filter((i) => matchStatus(i, filterStatus))
    .sort((a, b) => {
      const diff = (seqOf(a) - seqOf(b)) || String(a.issue_date).localeCompare(String(b.issue_date));
      return newestFirst ? -diff : diff;
    });

  const pages = perPage === 0 ? 1 : Math.max(1, Math.ceil(filtered.length / perPage));
  const curPage = Math.min(page, pages);
  const shown = perPage === 0 ? filtered : filtered.slice((curPage - 1) * perPage, curPage * perPage);

  // Meseci koji postoje u izabranoj godini (za filter "Mesec")
  const months = useMemo(() => {
    const set = new Set(yearDocs.map((i) => String(i.issue_date || '').slice(0, 7)).filter(Boolean));
    return [...set].sort().reverse();
  }, [yearDocs]);
  const monthLabel = (key) => {
    const [y, m] = key.split('-').map(Number);
    if (!y || !m) return key;
    const name = new Date(y, m - 1, 1).toLocaleDateString(locale, { month: 'long' });
    return `${name.charAt(0).toUpperCase()}${name.slice(1)}`;
  };
  const docWord = (n) => {
    if (lang === 'en') return n === 1 ? 'document' : 'documents';
    if (lang === 'ru') {
      const d = n % 10, h = n % 100;
      if (d === 1 && h !== 11) return 'документ';
      if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'документа';
      return 'документов';
    }
    const d = n % 10, h = n % 100;
    if (d === 1 && h !== 11) return 'dokument';
    if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'dokumenta';
    return 'dokumenata';
  };

  const onStatusChanged = (u) => setInvoices((prev) => prev.map((i) => (i.id === u.id ? u : i)));

  if (loading) return <div style={styles.loading}>{t('loadingGeneric')}</div>;

  const filtersActive = filterKind !== 'sve' || filterStatus !== 'sve' || filterMonth !== 'sve' || q;
  const clearFilters = () => { setFilterKind('sve'); setFilterStatus('sve'); setFilterMonth('sve'); setQuery(''); };
  const pct = (v, max) => `${Math.min(100, Math.max(0, (v / max) * 100))}%`;

  return (
    <div>
      {/* Naslov i dugmad */}
      <div style={styles.headerRow} className="inv-header">
        <h1 style={styles.heading}>{vrsta === 'profaktura' ? tr('Profakture', 'Proformas', 'Проформы') : vrsta === 'faktura' ? tr('Fakture', 'Invoices', 'Счета') : t('invoicing')} <span style={styles.yearTag}>{year}</span></h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} className="inv-head-btns">
          <button onClick={() => setShowImport(true)} style={styles.importBtn}>
            <Icon name="download" size={15} style={{ transform: 'rotate(180deg)' }} />
            {tr('Uvezi iz Excela')}
          </button>
          <button onClick={() => navigate(vrsta === 'profaktura' ? '/fakturisanje/nova-profaktura' : '/fakturisanje/nova')} style={{ ...styles.addBtn, minWidth: 152, justifyContent: 'center' }}>
            <Icon name="plus" size={15} strokeWidth={2.2} />
            {vrsta === 'profaktura' ? tr('Nova profaktura', 'New proforma', 'Новая проформа') : vrsta === 'faktura' ? tr('Nova faktura', 'New invoice', 'Новый счёт') : t('newDocument').replace(/^\+\s*/, '')}
          </button>
        </div>
      </div>

      {/* Tri kartice sa limitima */}
      <div style={styles.stats} className="inv-stats">
        <div style={styles.stat} className="glow-card">
          <div style={styles.statHead}>
            <span style={styles.statLabel}>{tr(`Fakturisano u ${year}.`, `Invoiced in ${year}`, `Выставлено в ${year}`)}</span>
            <span style={styles.statTag}>{tr('LIMIT 6 MIL.', 'LIMIT 6M', 'ЛИМИТ 6 МЛН')}</span>
          </div>
          <div style={styles.statValue}>{formatRSD(totalYear)}</div>
          <div style={styles.bar}><div style={{ ...styles.barFill, width: pct(totalYear, LIMIT_YEAR) }} /></div>
          <div style={styles.statSub}>{tr('Preostalo do limita', 'Left to limit', 'До лимита')}: {formatRSD(Math.max(0, LIMIT_YEAR - totalYear))}</div>
        </div>
        <button
          style={{ ...styles.stat, ...styles.statBtn }}
          onClick={() => { setFilterStatus(overdueDocs.length ? 'kasni' : 'poslato'); }}
          title={tr('Prikaži fakture koje čekaju naplatu', 'Show unpaid invoices', 'Показать неоплаченные')}
        >
          <div style={styles.statHead}>
            <span style={styles.statLabel}>{tr('Čeka naplatu', 'Awaiting payment', 'Ожидает оплаты')}</span>
            <span style={styles.statTag}>{unpaidDocs.length} {docWord(unpaidDocs.length).toUpperCase()}</span>
          </div>
          <div style={{ ...styles.statValue, color: 'var(--ink-900)' }}>{formatRSD(unpaidSum)}</div>
          <div style={{ ...styles.statSub, ...(overdueDocs.length ? { color: 'var(--red-600)', fontWeight: 700 } : {}) }}>
            {overdueDocs.length
              ? tr(`Kasni posle roka: ${overdueDocs.length} · ${formatRSD(overdueSum)}`, `Overdue: ${overdueDocs.length} · ${formatRSD(overdueSum)}`, `Просрочено: ${overdueDocs.length} · ${formatRSD(overdueSum)}`)
              : tr('Nijedna ne kasni posle roka', 'Nothing overdue', 'Просрочек нет')}
          </div>
        </button>
        <div style={styles.stat}>
          <div style={styles.statHead}>
            <span style={styles.statLabel}>{tr('Naplaćeno (365 dana)', 'Collected (365 days)', 'Оплачено (365 дней)')}</span>
            <span style={styles.statTag}>{tr('LIMIT 8 MIL.', 'LIMIT 8M', 'ЛИМИТ 8 МЛН')}</span>
          </div>
          <div style={styles.statValue}>{formatRSD(paid365)}</div>
          <div style={styles.bar}><div style={{ ...styles.barFill, width: pct(paid365, LIMIT_365) }} /></div>
          <div style={styles.statSub}>{tr('Preostalo do limita', 'Left to limit', 'До лимита')}: {formatRSD(Math.max(0, LIMIT_365 - paid365))}</div>
        </div>
      </div>

      {/* Lista: posebna kartica, jasno odvojena od kartica sa limitima */}
      <div style={styles.card}>
        <div style={styles.listHead}>
          <h2 style={styles.listTitle}>{tr('Lista dokumenata', 'Documents', 'Документы')}</h2>
          <span style={styles.listCount}>{filtered.length} {docWord(filtered.length)}</span>
        </div>

        {/* Status: dugmići sa brojem */}
        <div style={styles.tabs} className="inv-tabs" role="group" aria-label={tr('Status', 'Status', 'Статус')}>
          {STATUS_TABS.map(([key, label]) => {
            const on = filterStatus === key;
            const n = tabCount(key);
            const late = key === 'kasni';
            return (
              <button
                key={key}
                onClick={() => setFilterStatus(key)}
                aria-pressed={on}
                style={{ ...styles.tab, ...(late && !on ? styles.tabLate : {}), ...(on ? styles.tabOn : {}) }}
              >
                {label}
                <span style={{ ...styles.tabCount, ...(late && !on ? styles.tabCountLate : {}), ...(on ? styles.tabCountOn : {}) }}>{n}</span>
              </button>
            );
          })}
        </div>

        {/* Pretraga i padajući filteri */}
        <div style={styles.toolbar} className="inv-toolbar">
          <label style={styles.search} className="inv-search">
            <Icon name="search" size={15} color="var(--ink-500)" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tr('Pretraži po broju, klijentu ili opisu', 'Search by number, client or description', 'Поиск по номеру, клиенту или описанию')}
              aria-label={tr('Pretraga faktura', 'Search invoices', 'Поиск счетов')}
              style={styles.searchInput}
            />
          </label>
          <label style={styles.selectWrap} className="inv-sel">
            <span style={styles.selectLabel}>{tr('Vrsta', 'Type', 'Тип')}:</span>
            <select value={filterKind} onChange={(e) => setFilterKind(e.target.value)} style={styles.select}>
              <option value="sve">{t('filterAll')}</option>
              <option value="faktura">{t('filterInvoices')}</option>
              <option value="profaktura">{t('filterProforma')}</option>
              <option value="fiskalni">{t('filterFiscal')}</option>
            </select>
          </label>
          <label style={styles.selectWrap} className="inv-sel">
            <span style={styles.selectLabel}>{tr('Mesec', 'Month', 'Месяц')}:</span>
            <select value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} style={styles.select}>
              <option value="sve">{tr('Svi', 'All', 'Все')}</option>
              {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
            </select>
          </label>
          <label style={styles.selectWrap} className="inv-sel">
            <span style={styles.selectLabel}>{tr('Redosled', 'Order', 'Порядок')}:</span>
            <select value={newestFirst ? 'new' : 'old'} onChange={(e) => setNewestFirst(e.target.value === 'new')} style={styles.select}>
              <option value="new">{tr('Najnovije', 'Newest', 'Новые')}</option>
              <option value="old">{tr('Najstarije', 'Oldest', 'Старые')}</option>
            </select>
          </label>
          <label style={styles.selectWrap} className="inv-sel inv-perpage">
            <span style={styles.selectLabel}>{tr('Prikaži', 'Show', 'Показать')}:</span>
            <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} style={styles.select}>
              {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              <option value={0}>{tr('Sve', 'All', 'Все')}</option>
            </select>
          </label>
        </div>

        {filtered.length === 0 ? (
          <div style={styles.empty}>
            {t('noDocuments')}
            {filtersActive && (
              <div style={{ marginTop: 12 }}>
                <button onClick={clearFilters} style={styles.clearBtn}>{tr('Poništi filtere', 'Clear filters', 'Сбросить фильтры')}</button>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="inv-grid inv-head" style={styles.head}>
              <span>{tr('Broj', 'Number', 'Номер')}</span>
              <span>{tr('Klijent i opis', 'Client and description', 'Клиент и описание')}</span>
              <span style={{ textAlign: 'right' }}>{tr('Datum', 'Date', 'Дата')}</span>
              <span style={{ textAlign: 'right' }}>{tr('Rok', 'Due', 'Срок')}</span>
              <span style={{ textAlign: 'right' }}>{tr('Iznos', 'Amount', 'Сумма')}</span>
              <span style={{ paddingLeft: 12 }}>{tr('Status', 'Status', 'Статус')}</span>
              <span />
            </div>

            {shown.map((inv) => {
              const late = overdueDays(inv);
              const foreign = inv.type === 'inostrana';
              const showKind = inv.document_kind && inv.document_kind !== 'faktura';
              return (
                <div
                  key={inv.id}
                  className="inv-grid inv-row"
                  onClick={() => navigate(`/fakturisanje/${inv.id}/pregled`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/fakturisanje/${inv.id}/pregled`); }}
                  tabIndex={0}
                  role="link"
                  aria-label={`${KIND_LABELS[inv.document_kind] || t('kindInvoice')} ${inv.invoice_number || ''}`}
                >
                  <div className="inv-c-no" style={styles.no}>
                    {inv.invoice_number || '—'}
                    {inv.sef_invoice_id && (
                      <span style={styles.sefTag} title={`eFaktura (SEF)${inv.sef_env === 'demo' ? ' · demo' : ''} · ${sefLabel(inv.sef_status)}`}>
                        <span style={styles.sefDot} />eFaktura{inv.sef_env === 'demo' ? ' · demo' : ''}
                      </span>
                    )}
                  </div>

                  <div className="inv-c-client" style={{ minWidth: 0 }}>
                    <div style={styles.client}>
                      {inv.client || tr('Bez klijenta', 'No client', 'Без клиента')}
                      {showKind && <span style={styles.kindTag}>{KIND_LABELS[inv.document_kind]}</span>}
                      {foreign && <span style={styles.curTag}>{inv.currency}</span>}
                    </div>
                    {inv.description && <div style={styles.desc}>{inv.description}</div>}
                  </div>

                  <div className="inv-c-date" style={styles.date}>{fmtDate(inv.issue_date)}</div>

                  <div className="inv-c-due" style={{ ...styles.date, ...(late > 0 ? styles.dueLate : {}) }}>
                    {inv.due_date ? fmtDate(inv.due_date) : '—'}
                  </div>

                  <div className="inv-c-amount" style={styles.amount}>
                    {foreign ? formatCur(inv.amount, inv.currency) : formatRSD(inv.amount)}
                    {foreign && <div style={styles.amountSub}>= {formatRSD(toRSD(inv))}</div>}
                  </div>

                  <div className={`inv-c-status${late > 0 ? ' has-late' : ''}`} style={{ paddingLeft: 12 }}>
                    {inv.document_kind !== 'profaktura' && !(late > 0) && <StatusBadge status={inv.status} />}
                    {inv.document_kind === 'profaktura' && invoiceOfProforma[inv.id] && (
                      <span style={styles.invoicedTag} title={tr(`Faktura ${invoiceOfProforma[inv.id].invoice_number || ''}`, `Invoice ${invoiceOfProforma[inv.id].invoice_number || ''}`, `Счёт ${invoiceOfProforma[inv.id].invoice_number || ''}`)}>
                        <Icon name="receipt" size={13} />{tr('Fakturisano', 'Invoiced', 'Выставлен счёт')}
                      </span>
                    )}
                    {late > 0 && (
                      <span className="inv-late" style={styles.lateBadge}>
                        <span style={styles.lateDot} />
                        {tr(`Kasni ${late} d.`, `${late} d overdue`, `Просрочено ${late} дн.`)}
                      </span>
                    )}
                  </div>

                  <div className="inv-c-actions" style={styles.actions} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                    <StatusActions invoice={inv} onChange={onStatusChanged} linkedInvoice={invoiceOfProforma[inv.id]} />
                  </div>
                </div>
              );
            })}

            <div style={styles.footer} className="inv-footer">
              <span>
                {perPage === 0 || filtered.length <= perPage
                  ? tr(`Prikazano: ${filtered.length}`, `Showing: ${filtered.length}`, `Показано: ${filtered.length}`)
                  : tr(
                    `Prikazano ${(curPage - 1) * perPage + 1}–${Math.min(curPage * perPage, filtered.length)} od ${filtered.length}`,
                    `Showing ${(curPage - 1) * perPage + 1}–${Math.min(curPage * perPage, filtered.length)} of ${filtered.length}`,
                    `Показано ${(curPage - 1) * perPage + 1}–${Math.min(curPage * perPage, filtered.length)} из ${filtered.length}`,
                  )}
              </span>
              {pages > 1 && (
                <span style={styles.pager}>
                  <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={curPage === 1} style={styles.pageBtn} aria-label={tr('Prethodna strana', 'Previous page', 'Назад')}>‹</button>
                  {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                    <button key={n} onClick={() => setPage(n)} style={{ ...styles.pageBtn, ...(n === curPage ? styles.pageOn : {}) }}>{n}</button>
                  ))}
                  <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={curPage === pages} style={styles.pageBtn} aria-label={tr('Sledeća strana', 'Next page', 'Вперёд')}>›</button>
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {showImport && <ImportInvoices onClose={() => setShowImport(false)} onDone={() => setReloadKey((k) => k + 1)} />}

      <style>{`
        .inv-grid {
          display: grid;
          grid-template-columns: 92px minmax(0, 1fr) 96px 96px 150px 128px 74px;
          column-gap: 12px;
          align-items: center;
          padding: 11px 14px;
        }
        .inv-row { border-bottom: 1px solid var(--line); cursor: pointer; }
        /* Brojevi i iznosi: cifre iste širine; zaglavlje tabele u mono oznakama */
        .inv-c-no, .inv-c-date, .inv-c-due, .inv-c-amount { font-variant-numeric: tabular-nums; }
        .inv-head { font-family: var(--font-mono); }
        .inv-head { letter-spacing: 0.1em !important; font-weight: 500 !important; }
        .inv-row:hover { background: var(--paper); }
        .inv-row:focus { outline: none; }
        .inv-row:focus-visible { outline: 2px solid var(--lavender-700); outline-offset: -2px; }
        .inv-c-actions { justify-self: end; }
        .inv-tabs { scrollbar-width: none; }
        .inv-tabs::-webkit-scrollbar { display: none; }
        @media (max-width: 1100px) {
          .inv-grid { grid-template-columns: 84px minmax(0, 1fr) 90px 90px 130px 118px 74px; }
        }
        @media (max-width: 900px) {
          .inv-stats { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 760px) {
          /* Dugmad gore: pola-pola, preko cele širine */
          .inv-header { flex-direction: column; align-items: stretch !important; }
          .inv-head-btns { width: 100%; }
          .inv-head-btns > button { flex: 1 1 0; justify-content: center; }
          /* Status dugmići: jedan red koji se pomera levo-desno */
          .inv-tabs { flex-wrap: nowrap !important; overflow-x: auto; margin: 0 -14px; padding: 0 14px 2px !important; }
          .inv-tabs > button { flex-shrink: 0; }
          /* Filteri: pretraga preko cele širine, padajući po dva u redu */
          .inv-search { flex: 1 1 100% !important; }
          .inv-search input, .inv-sel select { font-size: 16px !important; } /* iPhone ne zumira polja od 16px */
          .inv-sel { flex: 1 1 calc(50% - 4px) !important; min-width: 0; }
          .inv-sel select { min-width: 0; flex: 1; }
          /* Red fakture na telefonu: 1) broj i iznos krupno, 2) klijent i datum, 3) opis, status u boji i mala dugmad */
          .inv-head { display: none !important; }
          .inv-c-due { display: none !important; }
          .inv-row {
            grid-template-columns: minmax(0, 1fr) auto auto;
            grid-template-areas:
              "no amount amount"
              "client date date"
              "client status actions";
            row-gap: 3px;
            column-gap: 10px;
            padding: 12px 14px;
          }
          .inv-c-no { grid-area: no; font-size: 17px !important; font-weight: 800 !important; letter-spacing: -0.01em; }
          .inv-c-amount { grid-area: amount; align-self: baseline; font-size: 16px !important; font-weight: 800 !important; }
          .inv-c-client { grid-area: client; align-self: start; }
          .inv-c-client > div:first-child { color: var(--ink-700) !important; }
          .inv-c-date { grid-area: date; justify-self: end; align-self: start; font-size: 12px !important; color: var(--ink-500) !important; }
          .inv-c-status { grid-area: status; justify-self: end; align-self: end; padding-left: 0 !important; }
          .inv-c-actions { grid-area: actions; align-self: end; }
          /* Status bez "pilule": samo tačka i tekst u boji */
          .inv-c-status > span { background: transparent !important; padding: 0 !important; }
          .inv-c-status > span > span { border-radius: 50% !important; }
          /* Dugmad ✓ i ⋮: manja i bela */
          .inv-c-actions .st-icon-btn { width: 32px !important; height: 32px !important; border-radius: 9px !important; background: var(--surface) !important; border: 1px solid var(--line) !important; }
          .inv-c-actions .st-icon-btn svg { width: 16px; height: 16px; }
          .inv-footer { flex-direction: column; align-items: flex-start !important; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  loading: { padding: 40, textAlign: 'center', color: 'var(--ink-500)' },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, gap: 12 },
  heading: { fontSize: 'var(--fs-page, 22px)', color: 'var(--ink-900)', margin: 0 },
  yearTag: { fontSize: 13, fontWeight: 700, color: 'var(--teal-800)', background: 'var(--lavender-100)', borderRadius: 8, padding: '3px 9px', marginLeft: 6, verticalAlign: 'middle' },
  importBtn: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, background: 'var(--surface)', color: 'var(--teal-800)', border: '1px solid var(--line)', borderRadius: 10, padding: '0 14px', fontWeight: 700, fontSize: 'var(--fs-table, 13px)' },
  addBtn: { display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, background: 'var(--teal-800)', color: '#FFFFFF', border: 'none', borderRadius: 10, padding: '0 14px', fontWeight: 700, fontSize: 'var(--fs-table, 13px)' },

  stats: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, marginBottom: 32 },
  stat: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left' },
  statBtn: { cursor: 'pointer', font: 'inherit', color: 'inherit' },
  statHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  statLabel: { fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 'var(--fs-micro, 11px)', fontWeight: 500, color: 'var(--ink-500)' },
  statTag: { fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-micro, 11px)', fontWeight: 500, letterSpacing: '0.06em', color: 'var(--ink-500)', background: 'var(--paper)', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' },
  statValue: { fontSize: 'var(--fs-number, 20px)', fontWeight: 600, color: 'var(--ink-900)', letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' },
  bar: { height: 6, borderRadius: 99, background: 'var(--line)', overflow: 'hidden' },
  barFill: { height: '100%', background: 'var(--teal-800)', borderRadius: 99 },
  statSub: { fontSize: 'var(--fs-small, 12px)', color: 'var(--ink-500)' },

  card: { background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '20px 20px 6px' },
  listHead: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 16 },
  listTitle: { fontSize: 'var(--fs-section, 16px)', fontWeight: 800, color: 'var(--ink-900)', margin: 0 },
  listCount: { fontSize: 'var(--fs-small, 12px)', color: 'var(--ink-500)' },
  tabs: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 },
  tab: { display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 32, padding: '0 11px', borderRadius: 9, border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--ink-700)', fontSize: 'var(--fs-table, 13px)', fontWeight: 700, cursor: 'pointer' },
  tabOn: { background: 'var(--teal-800)', borderColor: 'var(--teal-800)', color: '#fff' },
  tabLate: { color: 'var(--red-600)', borderColor: 'var(--line)' },
  tabCount: { minWidth: 18, padding: '1px 6px', borderRadius: 6, fontSize: 'var(--fs-micro, 11px)', textAlign: 'center', background: 'var(--paper)', color: 'var(--ink-700)' },
  tabCountOn: { background: 'rgba(255,255,255,0.18)', color: '#fff' },
  tabCountLate: { background: 'var(--red-100)', color: 'var(--red-600)' },

  toolbar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--line)' },
  search: { flex: '1 1 220px', display: 'flex', alignItems: 'center', gap: 8, minHeight: 36, padding: '0 12px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10 },
  searchInput: { flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 'var(--fs-body, 14px)', color: 'var(--ink-900)' },
  selectWrap: { display: 'flex', alignItems: 'center', gap: 4, minHeight: 36, padding: '0 4px 0 10px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10 },
  selectLabel: { fontSize: 'var(--fs-small, 12px)', fontWeight: 600, color: 'var(--ink-500)', whiteSpace: 'nowrap' },
  select: { border: 'none', background: 'transparent', fontSize: 'var(--fs-table, 13px)', fontWeight: 700, color: 'var(--teal-800)', padding: '4px 2px', cursor: 'pointer' },
  clearBtn: { border: '1px solid var(--line)', background: 'var(--surface)', color: 'var(--teal-800)', borderRadius: 10, padding: '8px 14px', fontWeight: 700, fontSize: 13 },

  empty: { textAlign: 'center', padding: '44px 20px', color: 'var(--ink-500)', fontSize: 14 },
  head: { fontSize: 'var(--fs-micro, 11px)', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink-500)', background: 'var(--paper)', borderRadius: 8 },

  no: { fontSize: 'var(--fs-table, 13px)', fontWeight: 800, color: 'var(--teal-800)', whiteSpace: 'nowrap' },
  client: { fontSize: 'var(--fs-table, 13px)', fontWeight: 600, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  desc: { fontSize: 'var(--fs-small, 12px)', color: 'var(--ink-500)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  // Oznaka ispod broja fakture (ne širi kolonu, ne preklapa klijenta)
  sefTag: { display: 'flex', alignItems: 'center', gap: 5, marginTop: 3, fontSize: 11, fontWeight: 600, color: 'var(--lavender-700)', whiteSpace: 'nowrap' },
  sefDot: { width: 6, height: 6, borderRadius: '50%', background: 'var(--lavender-700)', flexShrink: 0 },
  invoicedTag: { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 8, background: 'var(--teal-100)', color: 'var(--teal-800)', whiteSpace: 'nowrap' },
  kindTag: { fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: 'var(--gold-100)', color: 'var(--gold-600)', marginLeft: 6, verticalAlign: 'middle' },
  curTag: { fontSize: 10.5, fontWeight: 700, padding: '1px 6px', borderRadius: 5, background: 'var(--lavender-100)', color: 'var(--lavender-700)', marginLeft: 6, verticalAlign: 'middle' },
  date: { fontSize: 'var(--fs-table, 13px)', color: 'var(--ink-700)', whiteSpace: 'nowrap', textAlign: 'right' },
  dueLate: { color: 'var(--red-600)', fontWeight: 700 },
  lateBadge: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, borderRadius: 7, padding: '3px 9px', background: 'var(--red-100)', color: 'var(--red-600)', whiteSpace: 'nowrap' },
  lateDot: { width: 7, height: 7, borderRadius: '50%', background: 'var(--red-600)', flexShrink: 0 },
  amount: { fontSize: 'var(--fs-table, 13px)', fontWeight: 700, color: 'var(--ink-900)', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
  amountSub: { fontSize: 11, color: 'var(--ink-500)', fontWeight: 500, marginTop: 1 },
  actions: { display: 'flex', justifyContent: 'flex-end' },
  footer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 4px', fontSize: 'var(--fs-small, 12px)', color: 'var(--ink-500)' },
  pager: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  pageBtn: { minWidth: 30, height: 30, padding: '0 8px', border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink-700)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  pageOn: { background: 'var(--teal-800)', borderColor: 'var(--teal-800)', color: '#fff' },
};
